import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const SiteContentContext = createContext();

export const useSiteContent = () => useContext(SiteContentContext);

export const SiteContentProvider = ({ children }) => {
    const [images, setImages] = useState({});
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);

    const fetchContent = async () => {
        try {
            setLoading(true);
            // Fetch Images
            const { data: imagesData, error: imagesError } = await supabase
                .from('images')
                .select('key, url, label, page');

            if (imagesData) {
                const imgMap = imagesData.reduce((acc, curr) => {
                    acc[curr.key] = curr.url;
                    return acc;
                }, {});
                setImages(imgMap);
            }

            // Fetch Settings
            const { data: settingsData, error: settingsError } = await supabase
                .from('site_settings')
                .select('key, value');

            if (settingsData) {
                const settingsMap = settingsData.reduce((acc, curr) => {
                    acc[curr.key] = curr.value;
                    return acc;
                }, {});
                setSettings(settingsMap);
            }

        } catch (error) {
            console.error("Error fetching site content:", error);
        } finally {
            setLoading(false);
        }
    };

    const updateImage = async (key, url) => {
        // Optimistic update
        setImages(prev => ({ ...prev, [key]: url }));

        const { error } = await supabase
            .from('images')
            .upsert({ key, url });

        if (error) {
            console.error("Error updating image:", error);
            // Revert on error? Or just let the next fetch handle it.
        }
    };

    const updateSetting = async (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));

        const { error } = await supabase
            .from('site_settings')
            .upsert({ key, value });

        if (error) console.error("Error updating setting:", error);
    };

    useEffect(() => {
        fetchContent();
    }, []);

    // Helper to get image with fallback
    const getImage = (key, fallback) => images[key] || fallback;

    // Helper to get setting with fallback
    const getSetting = (key, fallback) => settings[key] !== undefined ? settings[key] : fallback;

    return (
        <SiteContentContext.Provider value={{
            images,
            settings,
            loading,
            updateImage,
            updateSetting,
            getImage,
            getSetting,
            fetchContent
        }}>
            {children}
        </SiteContentContext.Provider>
    );
};
